import { INGREDIENTS_BY_ID, resolveIngredientName } from '../../data/index.js';
import type { RecipeDraft } from '../../domain/recipeDraft.js';
import { validateImport } from '../../domain/recipeImport.js';

export type PasteOutcome =
  | { ok: true; draft: RecipeDraft; warnings: string[]; source: 'paste-text' | 'paste-url'; sourceUrl: string | null }
  | { ok: false; message: string };

export function looksLikeUrl(input: string): boolean {
  return /^https?:\/\/\S+$/i.test(input.trim());
}

export async function pasteRecipe(input: string, token: string, fetchImpl: typeof fetch = fetch): Promise<PasteOutcome> {
  const trimmed = input.trim();
  let response: Response;
  try {
    response = await fetchImpl('/api/parse-recipe', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(looksLikeUrl(trimmed) ? { url: trimmed } : { text: trimmed }),
    });
  } catch {
    return { ok: false, message: 'No connection. Type the recipe in, or try again when you have signal.' };
  }

  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok || !body) {
    const message = typeof body?.['error'] === 'string' ? body['error'] : `Couldn't read that (error ${response.status}).`;
    return { ok: false, message };
  }

  // The server validated this too. It is checked again here because this is
  // the last step before the form, and an unknown id must never reach it.
  const { draft, warnings } = validateImport(body['recipe'], INGREDIENTS_BY_ID, resolveIngredientName);
  if (draft.lines.length === 0) {
    return { ok: false, message: "Couldn't find any ingredients in that. Paste the ingredients list too." };
  }
  return {
    ok: true, draft, warnings,
    source: body['source'] === 'paste-url' ? 'paste-url' : 'paste-text',
    sourceUrl: typeof body['sourceUrl'] === 'string' ? body['sourceUrl'] : null,
  };
}
